import { Row, Col, Card } from "antd";
import { useEffect, useState } from "react";
import MaharashtraMap from "./Map";
import { NewLoginPage } from "./newLoginPage";
import TypingLoader from "./TypingLoader";
import "./pageAnimations.css";
import "./independenceDayTheme.css";
import { isIndependenceDayThemeActive } from "./independenceDayTheme";
import { Footer } from "antd/lib/layout/layout";
import {
  EnvironmentOutlined,
  CodeSandboxOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
} from "@ant-design/icons";

const AshokaChakra = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 100 100"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="3" />
    <circle cx="50" cy="50" r="6" fill="currentColor" />
    {Array.from({ length: 24 }).map((_, i) => {
      const angle = (i * 15 * Math.PI) / 180;
      const x2 = 50 + Math.cos(angle) * 42;
      const y2 = 50 + Math.sin(angle) * 42;
      return (
        <line
          key={i}
          x1="50"
          y1="50"
          x2={x2}
          y2={y2}
          stroke="currentColor"
          strokeWidth="1.5"
        />
      );
    })}
  </svg>
);

const ContactItem = ({ icon, text }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      marginBottom: "16px",
      fontSize: "16px",
    }}
  >
    <span
      style={{
        fontSize: "20px",
        color: "#1677ff",
        marginRight: "12px",
      }}
    >
      {icon}
    </span>
    <span>{text}</span>
  </div>
);

const NewMainPage = () => {
  const [loading, setLoading] = useState(true);
  const [hideLoader, setHideLoader] = useState(false);
  const [activeKey, setActiveKey] = useState(""); // keeps track of what is active
  const isIndDayTheme = isIndependenceDayThemeActive();

  useEffect(() => {
    document.body.style.overflow = "hidden";

    const timer = setTimeout(() => {
      setLoading(false);

      setTimeout(() => {
        setHideLoader(true);
        document.body.style.overflow = "";
      }, 550);
    }, 1000);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = "";
    };
  }, []);

  // Check if we should show the main content (map + login)
  const showMainContent = activeKey === "";
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileLogin, setShowMobileLogin] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className={`page-wrapper${isIndDayTheme ? " ind-day-theme" : ""}`}>
      {!hideLoader && <TypingLoader fadeOut={!loading} />}

      {/* MAIN CONTENT OR ACTIVE PAGE */}
      <div className={`page-content ${loading ? "blurred" : "unblur fade-in"}`}>
        {showMainContent ? (
          <>
            <>
              <Row className="main-split-row">
                {/* MAP SIDE */}
                <Col
                  xs={24}
                  md={14}
                  className={`map-side ${isMobile ? "mobile-full" : ""}`}
                >
                  {isIndDayTheme && (
                    <>
                      <div className="ind-day-banner">
                        <div className="ind-day-banner-row">
                          <div
                            className="ind-day-banner-flag"
                            aria-hidden="true"
                          >
                            <span />
                            <span />
                            <span />
                          </div>
                          <div className="ind-day-banner-text">
                            <p className="ind-day-banner-date">
                              <span className="ind-day-num">15</span>
                              <span className="ind-day-th">TH</span>
                              <span className="ind-day-month"> AUGUST</span>
                            </p>
                            <p className="ind-day-banner-title">
                              Happy Independence Day
                            </p>
                          </div>
                        </div>
                        <div className="ind-day-banner-line" />
                        <p className="ind-day-banner-tagline">
                          Freedom | Unity | Progress
                        </p>
                      </div>
                      <div className="ind-day-landmarks" aria-hidden="true" />
                      <AshokaChakra className="ind-day-chakra" />
                    </>
                  )}

                  <div className={isIndDayTheme ? "ind-day-map-wrap" : undefined}>
                    <MaharashtraMap independenceTheme={isIndDayTheme} />
                  </div>

                  {/* Show Login Button ONLY on Mobile */}
                  {isMobile && !showMobileLogin && (
                    <div className="mobile-login-btn">
                      <button onClick={() => setShowMobileLogin(true)}>
                        Login
                      </button>
                    </div>
                  )}
                </Col>

                {/* DESKTOP LOGIN SIDE */}
                {!isMobile && (
                  <Col xs={24} md={10} className="login-side slide-right">
                    <NewLoginPage independenceTheme={isIndDayTheme} />
                  </Col>
                )}
              </Row>

              {/* MOBILE LOGIN FULL SCREEN */}
              {isMobile && showMobileLogin && (
                <div className="mobile-login-page">
                  <div className="mobile-login-header">
                    <button
                      className="back-btn"
                      onClick={() => setShowMobileLogin(false)}
                    >
                      ← Back
                    </button>
                  </div>

                  <NewLoginPage independenceTheme={isIndDayTheme} />
                </div>
              )}
            </>
          </>
        ) : (
          <div style={{ margin: "auto" }}>
            {activeKey === "6" && (
              <div
                style={{
                  width: "100%",
                  minHeight: "100vh",
                  padding: "50px 60px",
                }}
              >
                {/* Page Header */}
                <h2
                  style={{
                    color: "#1677ff",
                    marginBottom: "40px",
                    fontSize: "36px",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Contact Us
                </h2>

                <Row justify="start">
                  <Col span={24}>
                    <Card
                      bordered={false}
                      style={{
                        padding: "30px",
                        borderRadius: "12px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      }}
                    >
                      <h2
                        style={{
                          color: "#1677ff",
                          marginBottom: "24px",
                          fontSize: "22px",
                        }}
                      >
                        I MARK TECHNOLOGY
                      </h2>

                      <ContactItem
                        icon={<EnvironmentOutlined />}
                        text="301, Vijayraj Marvel, Prem Nagar, Jule Solapur"
                      />

                      <ContactItem
                        icon={<EnvironmentOutlined />}
                        text="Solapur-413004"
                      />

                      <ContactItem
                        icon={<MailOutlined />}
                        text={
                          <a href="mailto:imarktechnology1@gmail.com">
                            imarktechnology1@gmail.com
                          </a>
                        }
                      />
                    </Card>
                  </Col>
                </Row>
              </div>
            )}

            {activeKey === "4" && (
              <>
                <h2
                  style={{
                    color: "#1677ff",
                    marginBottom: "40px",
                    fontSize: "36px",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Disclaimer
                </h2>

                <Row style={{ width: "80%", margin: "auto" }}>
                  <Col>
                    <Card
                      bordered={false}
                      style={{
                        padding: "30px",
                        borderRadius: "12px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      }}
                    >
                      <p>
                        <b>General Disclaimer</b>
                      </p>
                      <span>
                        CMIS is a secure, internal-use platform created
                        exclusively for use by the Police Department. It is
                        designed to maintain and analyze criminal records. While
                        every effort is made to ensure data accuracy, CMIS does{" "}
                        <b>not guarantee</b> that the information is error-free
                        or exhaustive.
                      </span>
                      <p>
                        <b>Limited Liability</b>
                      </p>
                      <span>
                        I MARK TECHNOLOGY and associated departments shall not
                        be held liable for:
                      </span>
                      <span>
                        <ul>
                          <li>
                            Any unauthorized access or misuse of data by users
                          </li>
                          <li>
                            Any legal or procedural action taken based solely on
                            CMIS data
                          </li>
                          <li>
                            Delays or system errors beyond reasonable control
                          </li>
                        </ul>
                      </span>
                      <p>
                        <b>Usage Responsibility</b>
                      </p>
                      <span>
                        <ul>
                          <li>
                            All users must ensure that the data they enter is
                            accurate and legally obtained
                          </li>
                          <li>
                            CMIS is to be used only for lawful purposes by
                            police personnel. Misuse or manipulation of the
                            system may result in disciplinary or legal action.
                          </li>
                        </ul>
                      </span>
                    </Card>
                  </Col>
                </Row>
              </>
            )}

            {activeKey === "5" && (
              <>
                <h2
                  style={{
                    color: "#1677ff",
                    marginBottom: "40px",
                    fontSize: "36px",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Privacy Policy
                </h2>

                <Row style={{ width: "80%", margin: "auto" }}>
                  <Col>
                    <Card
                      bordered={false}
                      style={{
                        padding: "30px",
                        borderRadius: "12px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      }}
                    >
                      <p>
                        <b>Introduction</b>
                      </p>
                      <span>
                        This Privacy Policy outlines how CMIS, developed and
                        maintained by <b>I MARK TECHNOLOGY</b>, collects,
                        stores, and uses information entered into the system by
                        authorized users from police departments.
                      </span>
                      <p>
                        <b>Data Collection</b>
                      </p>
                      <span>
                        CMIS may collect and store the following information:
                      </span>
                      <span>
                        <ul>
                          <li>
                            Criminal records including name, address, mobile
                            number, caste, FIR details, photographs, etc.
                          </li>
                          <li>
                            User credentials (Police Station ID, Unit Admin ID,
                            etc.)
                          </li>
                          <li>
                            Information related to patrolling, investigation,
                            and crime history
                          </li>
                          <li>Access logs and activity records of users</li>
                        </ul>
                      </span>
                      <p>
                        <b>Purpose of Data Usage</b>
                      </p>
                      <span>
                        The data is collected and stored for the following
                        purposes:
                      </span>
                      <span>
                        <ul>
                          <li>Maintaining accurate criminal records</li>
                          <li>
                            Enabling police officials to take preventive and
                            legal action
                          </li>
                          <li>
                            Supporting data-based analysis and reports for crime
                            reduction
                          </li>
                          <li>
                            Ensuring accountability and operational transparency
                          </li>
                        </ul>
                      </span>
                      <p>
                        <b>Data Access and Protection</b>
                      </p>
                      <span>
                        <ul>
                          <li>
                            Data is accessible only to{" "}
                            <b>authorized personnel</b>
                            based on roles (Super User, Unit Admin, Police
                            Station User)
                          </li>
                          <li>
                            All sensitive information is protected through
                            secure storage and access controls
                          </li>
                          <li>Regular backups are maintained</li>
                        </ul>
                      </span>
                      <p>
                        <b>Data Sharing</b>
                      </p>
                      <span>
                        <ul>
                          <li>
                            No data is shared with third parties except when
                            required by legal authority or under the supervision
                            of law enforcement agencies. .
                          </li>
                        </ul>
                      </span>
                      <p>
                        <b>Your Rights</b>
                      </p>
                      <span>
                        <ul>
                          <li>
                            Only authorized users can view or update specific
                            records
                          </li>
                          <li>
                            All data modifications are logged with user ID and
                            timestamp and IP address
                          </li>
                        </ul>
                      </span>
                      <p>
                        <b>Updates to the Policy</b>
                      </p>
                      <span>
                        We may modify this Privacy Policy as needed. Users will
                        be informed through the system interface in case of
                        significant changes.
                      </span>
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </div>
        )}
      </div>

      {/* FOOTER */}
      {isIndDayTheme ? (
        <Footer className="main-footer ind-day-footer">
          <div className="ind-day-footer-inner">
            <div className="ind-day-footer-left">
              <svg
                className="ind-day-police-emblem"
                viewBox="0 0 64 64"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <polygon
                  points="32,2 38,22 60,22 42,34 48,54 32,42 16,54 22,34 4,22 26,22"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="2.2"
                />
                <circle
                  cx="32"
                  cy="30"
                  r="10"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="1.8"
                />
                <path
                  d="M32 22 L34 28 L40 28 L35 32 L37 38 L32 34 L27 38 L29 32 L24 28 L30 28 Z"
                  fill="#ffffff"
                />
              </svg>
              <div className="ind-day-footer-brand">
                <p className="ind-day-footer-title">महाराष्ट्र पोलीस</p>
                <p className="ind-day-footer-motto">सेवा | संरक्षण | विश्वास</p>
              </div>
            </div>

            <div className="ind-day-footer-center">
              <span className="ind-day-footer-line" aria-hidden="true" />
              <span className="ind-day-footer-stars" aria-hidden="true">
                ★ ★
              </span>
              <p className="ind-day-footer-slogan">
                सुरक्षित समाज, सक्षम पोलीस
              </p>
              <span className="ind-day-footer-stars" aria-hidden="true">
                ★ ★
              </span>
              <span className="ind-day-footer-line" aria-hidden="true" />
            </div>

            <div className="ind-day-footer-right">
              <div className="ind-day-footer-cmis" aria-hidden="true">
                <span>CMIS</span>
              </div>
            </div>
          </div>

          <div className="ind-day-footer-links">
            <span onClick={() => setActiveKey("4")}>Disclaimer</span>
            <span className="sep">|</span>
            <span onClick={() => setActiveKey("5")}>Privacy Policy</span>
            <span className="sep">|</span>
            <span onClick={() => setActiveKey("6")}>Contact Us</span>
            {activeKey !== "" && (
              <>
                <span className="sep">|</span>
                <span className="home-link" onClick={() => setActiveKey("")}>
                  Home
                </span>
              </>
            )}
          </div>
        </Footer>
      ) : (
        <Footer className="main-footer">
          <div className="text-center">
            <div style={{ marginTop: 8 }}>
              <span
                style={{
                  margin: "0 10px",
                  cursor: "pointer",
                  color: "rgb(56, 78, 117)",
                  fontSize: "16px",
                }}
                onClick={() => setActiveKey("4")}
              >
                <b>Disclaimer</b>
              </span>
              |
              <span
                style={{
                  margin: "0 10px",
                  cursor: "pointer",
                  color: "rgb(56, 78, 117)",
                  fontSize: "16px",
                }}
                onClick={() => setActiveKey("5")}
              >
                <b>Privacy Policy</b>
              </span>
              |
              <span
                style={{
                  margin: "0 10px",
                  cursor: "pointer",
                  color: "rgb(56, 78, 117)",
                  fontSize: "16px",
                }}
                onClick={() => setActiveKey("6")}
              >
                <b>Contact Us</b>
              </span>
              {activeKey !== "" && (
                <>
                  {" | "}
                  <span
                    style={{
                      margin: "0 10px",
                      cursor: "pointer",
                      color: "red",
                      fontSize: "16px",
                    }}
                    onClick={() => setActiveKey("")}
                  >
                    <b>Home</b>
                  </span>
                </>
              )}
            </div>
            <p style={{ marginTop: 8, fontSize: "14px" }}>
              <b>2021-2026 Developed and Managed by I Mark Technology</b>
            </p>
          </div>
        </Footer>
      )}
    </div>
  );
};

export default NewMainPage;
